import {
  ArrowLeft,
  Cat,
  GitBranch,
  GitPullRequest,
  Clock,
  DollarSign,
  Globe,
  Trash2,
  ExternalLink,
  Copy,
  Ticket,
  Server,
  Activity,
  Moon,
  Network,
  Bot,
  RefreshCw,
  Sun,
} from "lucide-react";
import { useState } from "react";

import {
  useMoriartyEnvironment,
  useKeepMoriartyAwake,
  useMoriartyConfig,
  useDestroyMoriarty,
  useSendMoriartyPrompt,
  useSleepMoriarty,
  useSyncMoriartyModelCredentials,
  useWakeMoriarty,
} from "@/hooks/useMoriarty";
import { getMoriartyDisplayStatus } from "@/lib/moriarty";
import { useStore } from "@/lib/store";
import { formatUptime, formatCost } from "@/lib/utils";

import { SidebarToggleButton } from "../SidebarToggleButton";

function statusConfig(status: ReturnType<typeof getMoriartyDisplayStatus>) {
  switch (status) {
    case "starting":
      return {
        label: "BOOTING",
        color: "text-cyber-yellow",
        bg: "bg-cyber-yellow/10",
        border: "border-cyber-yellow/30",
      };
    case "ready":
      return {
        label: "ONLINE",
        color: "text-cyber-green",
        bg: "bg-cyber-green/10",
        border: "border-cyber-green/30",
      };
    case "warning":
      return {
        label: "IDLE",
        color: "text-cyber-yellow",
        bg: "bg-cyber-yellow/10",
        border: "border-cyber-yellow/30",
      };
    case "stopping":
      return {
        label: "SLEEPING",
        color: "text-cyber-yellow",
        bg: "bg-cyber-yellow/10",
        border: "border-cyber-yellow/30",
      };
    case "stopped":
      return {
        label: "ASLEEP",
        color: "text-cyber-muted",
        bg: "bg-cyber-muted/10",
        border: "border-cyber-border",
      };
    case "resuming":
      return {
        label: "WAKING",
        color: "text-cyber-cyan",
        bg: "bg-cyber-cyan/10",
        border: "border-cyber-cyan/30",
      };
    case "terminated":
      return {
        label: "TERMINATED",
        color: "text-cyber-muted",
        bg: "bg-cyber-muted/10",
        border: "border-cyber-border",
      };
    case "boot_failed":
    case "resume_failed":
    case "failed":
      return {
        label:
          status === "resume_failed"
            ? "RESUME FAILED"
            : status === "boot_failed"
              ? "BOOT FAILED · RETAINED"
              : "FAILED",
        color: "text-cyber-red",
        bg: "bg-cyber-red/10",
        border: "border-cyber-red/30",
      };
  }
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

export function MoriartyDetail(): React.JSX.Element {
  const envId = useStore((state) => state.activeMoriartyEnvId);
  const setView = useStore((state) => state.setView);
  const showToast = useStore((state) => state.showToast);
  const { data, isLoading, error } = useMoriartyEnvironment(envId);
  const { data: configData } = useMoriartyConfig();
  const destroyMutation = useDestroyMoriarty();
  const sendPromptMutation = useSendMoriartyPrompt();
  const keepAwakeMutation = useKeepMoriartyAwake();
  const sleepMutation = useSleepMoriarty();
  const syncModelCredentialsMutation = useSyncMoriartyModelCredentials();
  const wakeMutation = useWakeMoriarty();
  const [showDestroyConfirm, setShowDestroyConfirm] = useState(false);

  const env = data?.environment;
  const ocPassword = configData?.config?.opencode_server_password;
  const attachCommand = env
    ? `OPENCODE_PASSWORD='${(ocPassword ?? "").replace(/'/g, "'\\''")}' opencode --server http://${env.id}:4096${env.opencodeSessionId ? ` --session ${env.opencodeSessionId}` : " /workspace/bold"}`
    : "";

  function handleBack() {
    setView("moriarty");
    const url = new URL(window.location.href);
    url.searchParams.delete("moriarty");
    url.searchParams.set("view", "moriarty");
    window.history.pushState({}, "", url);
  }

  async function handleDestroy() {
    if (!envId || destroyMutation.isPending) return;
    try {
      await destroyMutation.mutateAsync(envId);
      showToast("Environment terminated", "success");
      handleBack();
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to terminate";
      showToast(msg, "error");
    }
    setShowDestroyConfirm(false);
  }

  async function handleSendPrompt() {
    if (!envId || sendPromptMutation.isPending) return;
    try {
      const response = await sendPromptMutation.mutateAsync(envId);
      showToast(
        getMoriartyDisplayStatus(response.environment) === "resume_failed"
          ? "Prompt queued; resume must recover before delivery"
          : response.environment.status === "stopped" ||
              response.environment.status === "stopping" ||
              response.environment.status === "resuming"
            ? "Environment is waking; prompt queued"
            : "Agent prompt sent",
        "success",
      );
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to send prompt";
      showToast(msg, "error");
    }
  }

  async function handleLifecycle(action: "sleep" | "wake") {
    if (!envId) return;
    try {
      if (action === "sleep") {
        await sleepMutation.mutateAsync(envId);
        showToast("Environment is going to sleep", "success");
      } else {
        await wakeMutation.mutateAsync(envId);
        showToast("Environment is waking", "success");
      }
    } catch (err) {
      showToast(err instanceof Error ? err.message : `Failed to ${action} environment`, "error");
    }
  }

  async function handleKeepAwake() {
    if (!envId) return;
    try {
      await keepAwakeMutation.mutateAsync(envId);
      showToast("Environment will stay awake for one hour", "success");
    } catch (err) {
      showToast(err instanceof Error ? err.message : "Failed to keep environment awake", "error");
    }
  }

  async function handleSyncModelCredentials() {
    if (!envId || syncModelCredentialsMutation.isPending) return;
    try {
      const response = await syncModelCredentialsMutation.mutateAsync(envId);
      if (response.result.status === "updated" || response.result.status === "current") {
        showToast(
          response.result.status === "updated"
            ? "Model credentials synchronized"
            : "Model credentials are already current",
          "success",
        );
      } else {
        showToast(
          response.result.message ?? "Model credentials could not be synchronized",
          "error",
        );
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : "Failed to synchronize model credentials";
      showToast(msg, "error");
    }
  }

  function copyToClipboard(text: string) {
    navigator.clipboard.writeText(text);
    showToast("Copied to clipboard", "success");
  }

  if (isLoading) {
    return (
      <div className="flex flex-1 items-center justify-center">
        <div className="text-cyber-muted">
          <span className="animate-spin-glow mr-2 inline-block">&#9680;</span>
          Loading Moriarty...
        </div>
      </div>
    );
  }

  if (error || !env) {
    return (
      <div className="flex flex-1 flex-col items-center justify-center gap-3">
        <Cat size={32} className="text-cyber-muted/30" />
        <p className="text-cyber-muted text-sm">Moriarty not found</p>
        <button
          onClick={handleBack}
          className="text-cyber-magenta hover:text-cyber-magenta/80 text-xs transition-all"
        >
          Back to network
        </button>
      </div>
    );
  }

  const displayStatus = getMoriartyDisplayStatus(env);
  const resumeFailed = displayStatus === "resume_failed";
  const status = statusConfig(displayStatus);
  const isAlive = env.status !== "terminated" && env.status !== "failed";
  const isReachable =
    !!env.tailscaleHostname && (env.status === "ready" || env.status === "warning");
  const hasPromptTemplate = !!env.ticketId || !!env.instructions?.trim();
  const canSendPrompt = isAlive && env.status !== "boot_failed" && hasPromptTemplate;
  const canSyncModelCredentials = isReachable;

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {/* Header */}
      <div className="border-cyber-border flex-shrink-0 border-b py-2 pr-2 pl-0 md:px-4 md:py-3">
        <div className="flex items-center justify-between px-2">
          <div className="flex items-center gap-3">
            <SidebarToggleButton />
            <button
              onClick={handleBack}
              className="text-cyber-muted hover:text-cyber-magenta p-1 transition-all"
            >
              <ArrowLeft size={16} />
            </button>
            <h2 className="text-cyber-text font-mono text-sm font-medium">{env.id}</h2>
            <span
              className={`rounded-full border px-2 py-0.5 text-[10px] ${status.bg} ${status.color} ${status.border} ${["starting", "stopping", "resuming"].includes(displayStatus) ? "animate-neon-pulse" : ""}`}
            >
              {status.label}
            </span>
          </div>
          {isAlive && (
            <div className="flex items-center gap-2">
              {(env.status === "ready" || env.status === "warning") && (
                <>
                  <button
                    onClick={handleKeepAwake}
                    disabled={keepAwakeMutation.isPending}
                    className="border-cyber-border text-cyber-muted hover:border-cyber-cyan/30 hover:text-cyber-cyan flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs transition-all disabled:opacity-60"
                  >
                    <Sun size={12} />
                    Keep awake
                  </button>
                  <button
                    onClick={() => handleLifecycle("sleep")}
                    disabled={sleepMutation.isPending}
                    className="border-cyber-yellow/30 text-cyber-yellow hover:bg-cyber-yellow/10 flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs transition-all disabled:opacity-60"
                  >
                    <Moon size={12} />
                    Sleep
                  </button>
                </>
              )}
              {(env.status === "stopped" || env.status === "stopping") && (
                <button
                  onClick={() => handleLifecycle("wake")}
                  disabled={wakeMutation.isPending}
                  className="border-cyber-cyan/30 text-cyber-cyan hover:bg-cyber-cyan/10 flex items-center gap-1.5 rounded border px-3 py-1.5 text-xs transition-all disabled:opacity-60"
                >
                  <Sun size={12} />
                  {env.status === "stopping" ? "Wake after stop" : "Wake"}
                </button>
              )}
              <button
                onClick={() => setShowDestroyConfirm(true)}
                className="flex items-center gap-1.5 rounded border border-red-500/30 px-3 py-1.5 text-xs text-red-400 transition-all hover:bg-red-500/10"
              >
                <Trash2 size={12} />
                Terminate
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto p-4">
        <div className="mx-auto max-w-3xl space-y-4">
          {env.status === "boot_failed" && (
            <div className="border-cyber-red/30 bg-cyber-red/10 text-cyber-red space-y-2 rounded-lg border p-4 text-sm">
              <p>
                Boot failed — instance retained and incurring charges. Terminate it manually after
                debugging.
              </p>
              <p className="text-xs">
                If Tailscale connected before the failure, inspect the boot log over SSH:
              </p>
              <code className="block text-xs">ssh ubuntu@{env.tailscaleHostname || env.id}</code>
              <code className="block text-xs">sudo tail -n 150 /var/log/moriarty/boot.log</code>
              <p className="text-xs">
                If SSH is unavailable, use the EC2 console for instance {env.instanceId}.
              </p>
            </div>
          )}
          {/* Connection panel — only for reachable environments */}
          {isReachable && (
            <>
              {/* Web apps */}
              <div className="border-cyber-magenta/20 bg-cyber-magenta/5 rounded-lg border p-4">
                <h3 className="text-cyber-magenta mb-3 flex items-center gap-2 text-xs tracking-wider uppercase">
                  <Globe size={12} />
                  Web Apps
                </h3>
                <div className="space-y-2">
                  {[
                    { label: "Backend API", sub: "be" },
                    { label: "Web App", sub: "app" },
                    { label: "Floripa (Admin)", sub: "floripa" },
                    { label: "Marketing Site", sub: "site" },
                  ].map(({ label, sub }) => {
                    const url = `https://${sub}.${env.id}.boldorg.app`;
                    return (
                      <div
                        key={sub}
                        className="bg-cyber-bg flex items-center justify-between rounded px-3 py-2"
                      >
                        <div>
                          <span className="text-cyber-muted block text-[10px]">{label}</span>
                          <code className="text-cyber-text text-xs">{url}</code>
                        </div>
                        <div className="flex items-center gap-1">
                          <button
                            onClick={() => copyToClipboard(url)}
                            className="text-cyber-muted hover:text-cyber-magenta p-1 transition-all"
                          >
                            <Copy size={12} />
                          </button>
                          <a
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-cyber-muted hover:text-cyber-magenta p-1 transition-all"
                          >
                            <ExternalLink size={12} />
                          </a>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Access & services */}
              <div className="border-cyber-border bg-cyber-surface/50 rounded-lg border p-4">
                <h3 className="text-cyber-muted mb-3 flex items-center gap-2 text-xs tracking-wider uppercase">
                  <Network size={12} />
                  Access & Services
                </h3>

                {/* SSH */}
                <div className="mb-3">
                  <div className="bg-cyber-bg flex items-center justify-between rounded px-3 py-2">
                    <div>
                      <span className="text-cyber-muted block text-[10px]">SSH</span>
                      <code className="text-cyber-text text-xs">ssh ubuntu@{env.id}</code>
                    </div>
                    <button
                      onClick={() => copyToClipboard(`ssh ubuntu@${env.id}`)}
                      className="text-cyber-muted hover:text-cyber-magenta p-1 transition-all"
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                </div>

                {/* Tailscale address */}
                <div className="mb-3">
                  <div className="bg-cyber-bg flex items-center justify-between rounded px-3 py-2">
                    <div>
                      <span className="text-cyber-muted block text-[10px]">Tailscale FQDN</span>
                      <code className="text-cyber-text text-xs">{env.tailscaleHostname}</code>
                    </div>
                    <button
                      onClick={() => copyToClipboard(env.tailscaleHostname!)}
                      className="text-cyber-muted hover:text-cyber-magenta p-1 transition-all"
                    >
                      <Copy size={12} />
                    </button>
                  </div>
                </div>

                {/* Agent */}
                <div className="mb-3">
                  <span className="text-cyber-muted/60 mb-2 block text-[10px] tracking-wider uppercase">
                    Agent
                  </span>

                  {/* OpenCode attach command */}
                  <div className="bg-cyber-bg mb-1.5 flex items-center justify-between rounded px-3 py-2">
                    <div>
                      <span className="text-cyber-muted block text-[10px]">OpenCode</span>
                      <code className="text-cyber-text text-xs">{attachCommand}</code>
                    </div>
                    <button
                      onClick={() => copyToClipboard(attachCommand)}
                      className="text-cyber-muted hover:text-cyber-magenta p-1 transition-all"
                    >
                      <Copy size={12} />
                    </button>
                  </div>

                  {/* Agent session indicator */}
                  {env.opencodeSessionId && (
                    <div className="bg-cyber-magenta/5 flex items-center gap-2 rounded px-3 py-1.5">
                      <Bot size={10} className="text-cyber-magenta" />
                      <span className="text-cyber-muted text-[10px]">Session</span>
                      <code className="text-cyber-magenta font-mono text-[10px]">
                        {env.opencodeSessionId}
                      </code>
                    </div>
                  )}

                  {canSendPrompt && (
                    <div className="border-cyber-magenta/20 bg-cyber-magenta/5 mt-3 rounded border p-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <span className="text-cyber-magenta/70 block text-[10px] tracking-wider uppercase">
                            Agent Prompt
                          </span>
                          <p className="text-cyber-text/80 mt-1 text-xs">
                            Re-send the startup prompt if the ready callback missed OpenCode.
                          </p>
                        </div>
                        <button
                          onClick={handleSendPrompt}
                          disabled={sendPromptMutation.isPending}
                          className="border-cyber-magenta/30 bg-cyber-magenta/10 text-cyber-magenta hover:bg-cyber-magenta/20 inline-flex items-center justify-center gap-1.5 rounded border px-3 py-1.5 text-xs transition-all disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <RefreshCw
                            size={12}
                            className={sendPromptMutation.isPending ? "animate-spin" : undefined}
                          />
                          {sendPromptMutation.isPending ? "Sending..." : "Re-send Prompt"}
                        </button>
                      </div>
                    </div>
                  )}

                  {canSyncModelCredentials && (
                    <div className="border-cyber-border bg-cyber-bg/40 mt-3 rounded border p-3">
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                        <div>
                          <span className="text-cyber-muted block text-[10px] tracking-wider uppercase">
                            Model Credentials
                          </span>
                          <p className="text-cyber-text/80 mt-1 text-xs">
                            Compare this environment with hosted OpenCode and apply hosted
                            credentials only when they are newer.
                          </p>
                        </div>
                        <button
                          onClick={handleSyncModelCredentials}
                          disabled={syncModelCredentialsMutation.isPending}
                          className="border-cyber-border bg-cyber-surface text-cyber-text hover:border-cyber-magenta/40 hover:text-cyber-magenta inline-flex items-center justify-center gap-1.5 rounded border px-3 py-1.5 text-xs transition-all disabled:cursor-not-allowed disabled:opacity-60"
                        >
                          <RefreshCw
                            size={12}
                            className={
                              syncModelCredentialsMutation.isPending ? "animate-spin" : undefined
                            }
                          />
                          {syncModelCredentialsMutation.isPending
                            ? "Syncing..."
                            : "Sync Credentials"}
                        </button>
                      </div>
                    </div>
                  )}
                </div>

                {/* Open ports */}
                <div>
                  <span className="text-cyber-muted/60 mb-2 block text-[10px] tracking-wider uppercase">
                    Open ports (via Tailscale)
                  </span>
                  <div className="grid grid-cols-2 gap-1.5">
                    {[
                      { label: "Postgres", port: "5432" },
                      { label: "Redis", port: "6379" },
                      { label: "ClickHouse", port: "8123" },
                      { label: "LocalStack", port: "4566" },
                    ].map(({ label, port }) => (
                      <div
                        key={port}
                        className="bg-cyber-bg flex items-center justify-between rounded px-3 py-1.5"
                      >
                        <div className="flex items-center gap-2">
                          <span className="text-cyber-muted text-[10px]">{label}</span>
                          <code className="text-cyber-text/60 text-[10px]">:{port}</code>
                        </div>
                        <button
                          onClick={() => copyToClipboard(`${env.tailscaleHostname}:${port}`)}
                          className="text-cyber-muted/40 hover:text-cyber-magenta p-0.5 transition-all"
                        >
                          <Copy size={10} />
                        </button>
                      </div>
                    ))}
                  </div>
                  <p className="text-cyber-muted/40 mt-2 text-[10px]">
                    Connect via Tailscale FQDN, e.g.{" "}
                    <code>psql -h {env.tailscaleHostname} -U bolddev bold_dev</code>
                  </p>
                </div>
              </div>
            </>
          )}

          {/* Starting progress */}
          {env.status === "starting" && (
            <div className="border-cyber-yellow/20 bg-cyber-yellow/5 rounded-lg border p-4">
              <div className="text-cyber-yellow flex items-center gap-2 text-sm">
                <span className="animate-spin-glow inline-block">&#9680;</span>
                <span>Environment is booting up... This typically takes 2-3 minutes.</span>
              </div>
              <div className="bg-cyber-bg mt-3 h-1 overflow-hidden rounded-full">
                <div className="moriarty-progress-bar bg-cyber-yellow/50 h-full rounded-full" />
              </div>
              <p className="text-cyber-yellow/50 mt-3 font-mono text-xs">
                Logs: /var/log/moriarty/boot.log | Script: /var/lib/cloud/instance/scripts/part-001
              </p>
            </div>
          )}

          {env.status === "warning" && (
            <div className="border-cyber-yellow/20 bg-cyber-yellow/5 text-cyber-yellow rounded-lg border p-4 text-xs">
              No meaningful activity has been detected.
              {env.wouldSleepAt
                ? ` Sleep is scheduled for ${formatDate(env.wouldSleepAt)}.`
                : ""}{" "}
              Use Keep awake to postpone it for one hour.
            </div>
          )}

          {env.status === "stopping" && (
            <div className="border-cyber-yellow/20 bg-cyber-yellow/5 text-cyber-yellow rounded-lg border p-4 text-xs">
              The EC2 instance is stopping. New agent work is queued and will wake it after the stop
              completes.
            </div>
          )}

          {env.status === "stopped" && (
            <div className="border-cyber-border bg-cyber-surface/50 text-cyber-muted rounded-lg border p-4 text-xs">
              The instance is asleep and is not accruing compute cost. Its volume and work are
              preserved; waking normally takes 2-3 minutes.
            </div>
          )}

          {resumeFailed && (
            <div className="border-cyber-red/30 bg-cyber-red/10 text-cyber-red space-y-2 rounded-lg border p-4 text-xs">
              <p>
                Resume failed — readiness has not been confirmed. The instance may still be running
                and accruing compute cost. Queued prompts are preserved and will wait until resume
                succeeds; queuing another prompt does not retry resume.
              </p>
              <p>Connect over SSH to inspect the failure and retry the resume service:</p>
              <code className="block">ssh ubuntu@{env.tailscaleHostname || env.id}</code>
              <code className="block">
                sudo journalctl -u moriarty-resume.service -b -n 100 --no-pager
              </code>
              <code className="block">sudo systemctl restart moriarty-resume.service</code>
              <p>Retrying refreshes credentials and restarts OpenCode.</p>
            </div>
          )}

          {displayStatus === "resuming" && (
            <div className="border-cyber-cyan/20 bg-cyber-cyan/5 text-cyber-cyan rounded-lg border p-4 text-xs">
              The instance is waking, refreshing credentials, and waiting for OpenCode. Queued
              prompts will be delivered when it reports ready.
            </div>
          )}

          {!isReachable && canSendPrompt && (
            <div className="border-cyber-magenta/20 bg-cyber-magenta/5 rounded-lg border p-3">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <p className="text-cyber-text/80 text-xs">
                  {resumeFailed
                    ? "You can queue the agent prompt now. Delivery will wait until resume succeeds."
                    : "Send the agent prompt now. Moriarty will persist it and wake the environment if needed."}
                </p>
                <button
                  onClick={handleSendPrompt}
                  disabled={sendPromptMutation.isPending}
                  className="border-cyber-magenta/30 bg-cyber-magenta/10 text-cyber-magenta hover:bg-cyber-magenta/20 inline-flex items-center justify-center gap-1.5 rounded border px-3 py-1.5 text-xs transition-all disabled:opacity-60"
                >
                  <RefreshCw
                    size={12}
                    className={sendPromptMutation.isPending ? "animate-spin" : undefined}
                  />
                  {sendPromptMutation.isPending ? "Queuing..." : "Queue Agent Prompt"}
                </button>
              </div>
            </div>
          )}

          {env.status === "ready" && !env.activityReporterVersion && (
            <div className="border-cyber-border bg-cyber-surface/50 text-cyber-muted rounded-lg border p-3 text-xs">
              Auto-sleep is disabled for this environment because no machine activity reporter has
              checked in. The lifecycle reconciler fails open in this state.
            </div>
          )}

          {/* Error message */}
          {env.errorMessage && (
            <div className="border-cyber-red/20 bg-cyber-red/5 rounded-lg border p-3">
              <p className="text-cyber-red font-mono text-xs">{env.errorMessage}</p>
            </div>
          )}

          {/* Info grid */}
          <div className="grid grid-cols-2 gap-3">
            <InfoCard icon={<GitBranch size={12} />} label="Branch" value={env.branch} />
            <InfoCard icon={<Server size={12} />} label="Instance" value={env.instanceType} />
            <InfoCard
              icon={<Clock size={12} />}
              label="Uptime"
              value={formatUptime(env.uptimeMinutes)}
            />
            <InfoCard
              icon={<DollarSign size={12} />}
              label="Cost"
              value={formatCost(env.estimatedCostUsd)}
            />
            <InfoCard icon={<Globe size={12} />} label="Created by" value={env.createdBy} />
            <InfoCard
              icon={<Activity size={12} />}
              label="Created"
              value={formatDate(env.createdAt)}
            />
            {env.ticketId && (
              <InfoCard
                icon={<Ticket size={12} />}
                label="Ticket"
                value={env.ticketId}
                href={env.ticketUrl ?? undefined}
              />
            )}
            {env.prNumber && (
              <InfoCard
                icon={<GitPullRequest size={12} />}
                label="Pull Request"
                value={`#${env.prNumber}`}
                href={env.prUrl ?? undefined}
              />
            )}
            {env.readyAt && (
              <InfoCard
                icon={<Clock size={12} />}
                label="Ready at"
                value={formatDate(env.readyAt)}
              />
            )}
          </div>

          {/* Instructions */}
          {env.instructions && (
            <div className="border-cyber-border bg-cyber-surface/50 rounded-lg border p-3">
              <h4 className="text-cyber-muted mb-2 text-[10px] tracking-wider uppercase">
                Instructions
              </h4>
              <p className="text-cyber-text text-xs whitespace-pre-wrap">{env.instructions}</p>
            </div>
          )}

          {/* Notes log */}
          {env.notes.length > 0 && (
            <div className="border-cyber-border bg-cyber-surface/50 rounded-lg border p-3">
              <h4 className="text-cyber-muted mb-2 text-[10px] tracking-wider uppercase">Notes</h4>
              <div className="space-y-1.5">
                {env.notes.map((note, i) => (
                  <div key={i} className="flex gap-2 text-xs">
                    <span className="text-cyber-muted/50 shrink-0">
                      {new Date(note.at).toLocaleTimeString()}
                    </span>
                    <span className="text-cyber-magenta/60 shrink-0 font-mono">{note.source}</span>
                    <span className="text-cyber-text">{note.message}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Termination info */}
          {env.terminatedAt && (
            <div className="border-cyber-border bg-cyber-surface/50 rounded-lg border p-3">
              <h4 className="text-cyber-muted mb-2 text-[10px] tracking-wider uppercase">
                Termination
              </h4>
              <div className="text-cyber-muted space-y-1 text-xs">
                <p>Terminated: {formatDate(env.terminatedAt)}</p>
                {env.terminationReason && <p>Reason: {env.terminationReason}</p>}
              </div>
            </div>
          )}

          {/* Metadata */}
          <div className="border-cyber-border bg-cyber-surface/50 rounded-lg border p-3">
            <h4 className="text-cyber-muted mb-2 text-[10px] tracking-wider uppercase">
              Technical Details
            </h4>
            <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
              <span className="text-cyber-muted">Environment ID</span>
              <span className="text-cyber-text font-mono">{env.id}</span>
              <span className="text-cyber-muted">Instance ID</span>
              <span className="text-cyber-text font-mono">{env.instanceId ?? "---"}</span>
              <span className="text-cyber-muted">AMI</span>
              <span className="text-cyber-text font-mono">{env.amiId}</span>
              <span className="text-cyber-muted">Tailscale</span>
              <span className="text-cyber-text font-mono">{env.tailscaleHostname ?? "---"}</span>
              {env.lastActivityAt && (
                <>
                  <span className="text-cyber-muted">Last activity</span>
                  <span className="text-cyber-text">{formatDate(env.lastActivityAt)}</span>
                </>
              )}
              {env.lastActivitySource && (
                <>
                  <span className="text-cyber-muted">Activity source</span>
                  <span className="text-cyber-text font-mono">{env.lastActivitySource}</span>
                </>
              )}
              {env.lastHeartbeatAt && (
                <>
                  <span className="text-cyber-muted">Reporter heartbeat</span>
                  <span className="text-cyber-text">{formatDate(env.lastHeartbeatAt)}</span>
                </>
              )}
              {env.activityReporterVersion && (
                <>
                  <span className="text-cyber-muted">Reporter version</span>
                  <span className="text-cyber-text font-mono">{env.activityReporterVersion}</span>
                </>
              )}
              {env.keepAwakeUntil && (
                <>
                  <span className="text-cyber-muted">Keep awake until</span>
                  <span className="text-cyber-text">{formatDate(env.keepAwakeUntil)}</span>
                </>
              )}
              {env.stoppedAt && (
                <>
                  <span className="text-cyber-muted">Stopped at</span>
                  <span className="text-cyber-text">{formatDate(env.stoppedAt)}</span>
                </>
              )}
              {env.modelAuthStatus && (
                <>
                  <span className="text-cyber-muted">Model auth</span>
                  <span className="text-cyber-text font-mono">{env.modelAuthStatus}</span>
                </>
              )}
              {env.modelAuthExpiresAt && (
                <>
                  <span className="text-cyber-muted">Model auth expires</span>
                  <span className="text-cyber-text">{formatDate(env.modelAuthExpiresAt)}</span>
                </>
              )}
              {env.modelAuthLastSyncedAt && (
                <>
                  <span className="text-cyber-muted">Model auth synced</span>
                  <span className="text-cyber-text">{formatDate(env.modelAuthLastSyncedAt)}</span>
                </>
              )}
              {env.modelAuthLastCheckedAt && (
                <>
                  <span className="text-cyber-muted">Model auth checked</span>
                  <span className="text-cyber-text">{formatDate(env.modelAuthLastCheckedAt)}</span>
                </>
              )}
              {env.modelAuthError && (
                <>
                  <span className="text-cyber-muted">Model auth detail</span>
                  <span className="text-cyber-red break-words">{env.modelAuthError}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Destroy confirmation modal */}
      {showDestroyConfirm && (
        <>
          <button
            type="button"
            aria-label="Close termination confirmation"
            className="fixed inset-0 z-40 bg-black/50"
            onClick={() => setShowDestroyConfirm(false)}
          />
          <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center">
            <div className="bg-cyber-surface pointer-events-auto w-80 rounded-lg border border-red-500/30 p-4">
              <div className="mb-3 flex items-center gap-2">
                <Cat size={16} className="text-red-400" />
                <h4 className="text-cyber-text text-sm">Terminate this Moriarty?</h4>
              </div>
              <p className="text-cyber-muted mb-4 text-xs">
                This will terminate <span className="text-cyber-text font-mono">{env.id}</span> and
                destroy the EC2 instance. This cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDestroyConfirm(false)}
                  className="text-cyber-muted hover:text-cyber-text px-3 py-1.5 text-sm transition-all"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDestroy}
                  disabled={destroyMutation.isPending}
                  className="rounded border border-red-500/30 bg-red-500/10 px-3 py-1.5 text-sm text-red-400 transition-all hover:bg-red-500/20"
                >
                  {destroyMutation.isPending ? "Terminating..." : "Terminate"}
                </button>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function InfoCard({
  icon,
  label,
  value,
  href,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  href?: string;
}) {
  return (
    <div className="border-cyber-border bg-cyber-surface/50 rounded-lg border p-3">
      <div className="text-cyber-muted mb-1 flex items-center gap-1.5">
        {icon}
        <span className="text-[10px] tracking-wider uppercase">{label}</span>
      </div>
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className="text-cyber-magenta flex items-center gap-1 text-sm hover:underline"
        >
          {value}
          <ExternalLink size={10} />
        </a>
      ) : (
        <p className="text-cyber-text truncate text-sm">{value}</p>
      )}
    </div>
  );
}
